<details class="group rounded-2xl border border-slate-900/10 bg-white shadow-sm">
  <summary class="flex cursor-pointer list-none flex-wrap items-center gap-2 p-4 text-[13px] [&::-webkit-details-marker]:hidden">
    <span class="inline-block transition-transform group-open:rotate-90" aria-hidden="true">&#9656;</span>
    <span class="text-sm font-bold">Tarife</span>
    @switch (phase()) {
      @case ('loading') { <span class="text-slate-500">Lädt…</span> }
      @case ('error') { <span class="text-red-700">{{ message() }}</span> }
      @default {
        <span class="text-slate-500">{{ tariffs()?.length ?? 0 }} Zeilen · {{ currentCount() }} von 4 Dimensionen aktuell belegt</span>
      }
    }
    <span class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">nur lesend</span>
  </summary>

  <div class="grid gap-4 border-t border-slate-100 p-4 text-[13px]">

    <!-- ── Dimensionen ─────────────────────────────────────────────────────── -->
    @if (tariffs(); as list) {
      @if (list.length === 0) {
        <p class="m-0 rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-900">
          Für diesen Standort ist kein Tarif hinterlegt; Bezugs- und Einspeisepreis werden nicht berechnet.
        </p>
      } @else {
        @for (group of groups(); track group.key) {
          <section class="grid gap-2">
            <h3 class="m-0 flex flex-wrap items-baseline gap-2 text-sm font-bold">
              {{ group.label }}
              <code class="text-[11px] font-normal text-slate-500">{{ group.key }}</code>
            </h3>

            @for (card of group.cards; track card.tariff.validFrom) {
              <article class="grid gap-2 rounded-xl border p-3"
                       [ngClass]="card.status === 'current' ? 'border-emerald-300 bg-emerald-50/40' : card.status === 'future' ? 'border-sky-200' : 'border-slate-200 opacity-70'">
                <div class="flex flex-wrap items-center gap-2">
                  @switch (card.status) {
                    @case ('current') { <span class="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-900">aktuell</span> }
                    @case ('future') { <span class="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-900">geplant</span> }
                    @case ('past') { <span class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">abgelöst</span> }
                  }
                  <span class="text-slate-600">{{ formatInstant(card.tariff.validFrom) }} → {{ formatInstant(card.tariff.validUntil) }}</span>
                </div>

                <dl class="m-0 grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] sm:grid-cols-4">
                  <div><dt class="text-slate-500">Preisart</dt><dd class="m-0 font-mono">{{ card.tariff.priceType }}</dd></div>
                  <div><dt class="text-slate-500">Richtung</dt><dd class="m-0 font-mono">{{ card.tariff.direction }}</dd></div>
                  <div><dt class="text-slate-500">Gültig ab</dt><dd class="m-0">{{ formatInstant(card.tariff.validFrom) }}</dd></div>
                  <div><dt class="text-slate-500">Art</dt><dd class="m-0 font-mono">{{ card.tariff.tariffKind }}</dd></div>
                  <div><dt class="text-slate-500">Name</dt><dd class="m-0">{{ card.tariff.tariffName ?? '–' }}</dd></div>
                  <div><dt class="text-slate-500">Einheit</dt><dd class="m-0">{{ unitLabel(card.tariff.unit) }}</dd></div>
                  <div><dt class="text-slate-500">Preis</dt><dd class="m-0 font-mono">{{ card.tariff.price ?? '–' }}</dd></div>
                  <div><dt class="text-slate-500">Aufschlag</dt><dd class="m-0 font-mono">{{ card.tariff.markup ?? '–' }}</dd></div>
                  <div><dt class="text-slate-500">Prozentsatz</dt><dd class="m-0 font-mono">{{ card.tariff.adjustmentPercent != null ? card.tariff.adjustmentPercent + ' %' : '–' }}</dd></div>
                  <div><dt class="text-slate-500">Marktpreis-Basis</dt><dd class="m-0 font-mono">{{ card.tariff.marketPriceBasis ?? '–' }}</dd></div>
                </dl>

                @if (card.tariff.bands.length > 0) {
                  <div class="overflow-x-auto">
                    <table class="w-full min-w-[480px] border-collapse text-[12px]">
                      <thead class="text-left text-[11px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th class="px-2 py-1 font-semibold">Start</th>
                          <th class="px-2 py-1 font-semibold">Preis</th>
                          <th class="px-2 py-1 font-semibold">Aufschlag</th>
                          <th class="px-2 py-1 font-semibold">Komponenten</th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (band of card.tariff.bands; track band.startTime) {
                          <tr class="border-t border-slate-100 align-top">
                            <td class="whitespace-nowrap px-2 py-1 font-mono">{{ band.startTime }}</td>
                            <td class="px-2 py-1 font-mono">{{ band.price ?? '–' }}</td>
                            <td class="px-2 py-1 font-mono">{{ band.markup ?? '–' }}</td>
                            <td class="px-2 py-1">
                              @for (component of componentEntries(band); track component.name) {
                                <div><code>{{ component.name }}</code> <span class="font-mono">{{ component.value }}</span></div>
                              } @empty {
                                <span class="text-slate-400">–</span>
                              }
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                }
              </article>
            } @empty {
              <p class="m-0 rounded-xl border border-dashed border-slate-300 p-3 text-slate-500">
                Keine Zeile; dieser Summand fehlt in der Preisformel.
              </p>
            }
          </section>
        }
      }
    }
  </div>
</details>
